import { NodeViewWrapper, useEditorState } from '@tiptap/react'
import { VFC, useEffect } from 'react'
import { useAsset } from '../../../queries'
import * as styles from '../styles'
import { AssetNodeProps, AssetNodeViewProps } from '../types'
import AssetImageNode from './Image'
import AssetResourceNode from './Resource'

const AssetNode: VFC<AssetNodeProps> = ({ nodeType, ...props }) => {
  if (!props.asset) return null

  if (!props.asset.assetUuid) {
    const normalizedNodeType = nodeType === 'resource' ? 'resource' : 'asset'
    return (
      <div css={styles.placeholder[normalizedNodeType]}>
        <div css={styles.placeholderBg}></div>
      </div>
    )
  }

  if (props.asset.assetType === 'image') {
    return <AssetImageNode {...props} asset={props.asset} />
  }

  return <AssetResourceNode {...props} asset={props.asset} />
}

const AssetNodeView: VFC<AssetNodeViewProps> = ({
  node,
  editor,
  extension,
  deleteNode,
  updateAttributes,
  selected,
}) => {
  const { onExpandAsset } = extension.options
  const { readOnly, assetStore, assetsLoading } = useEditorState({
    editor,
    selector: (snapshot) => ({
      readOnly: !snapshot.editor.isEditable,
      assetStore: snapshot.editor.storage.asset.assetStore,
      assetsLoading: snapshot.editor.storage.asset.assetsLoading,
    }),
  })

  const asset =
    assetStore.find((asset) => asset.assetUuid === node.attrs.assetUuid) ?? null

  // Fallback fetch for missing assets (IE: added via collaboration or stale cache)
  const shouldRefetch = asset === null && !assetsLoading

  const { data: refetchedAsset } = useAsset(node.attrs.assetUuid, shouldRefetch)

  // Reset expanded asset on unmount
  useEffect(() => {
    const onUnmount = () => onExpandAsset?.(null)
    editor.on('unmount', onUnmount)

    return () => {
      editor.off('unmount', onUnmount)
    }
  }, [editor, onExpandAsset])

  return (
    <NodeViewWrapper
      draggable={!readOnly}
      contentEditable={false}
      data-drag-handle={!readOnly ? '' : undefined}
    >
      <AssetNode
        asset={asset ?? refetchedAsset ?? null}
        editor={editor}
        node={node}
        nodeType={node.type.name}
        onDelete={deleteNode}
        onExpandAsset={onExpandAsset}
        readOnly={readOnly}
        selected={selected}
        updateAttributes={updateAttributes}
      />
    </NodeViewWrapper>
  )
}

export default AssetNodeView
